<template lang="pug">
v-container
  v-snackbar(v-model="showError" color="error")
    span {{ errorMessage }}
    v-btn(dark text @click="showError = false")
      v-icon fal fa-times
  v-snackbar(v-model="showSuccess" color="success")
    span {{ successMessage }}
    v-btn(dark text @click="showSuccess = false")
      v-icon fal fa-times
  v-dialog(v-model="showAutomaticOrderingConfirm" persistent max-width="480")
    v-card
      v-card-title {{$t('detailTab.automaticOrderingConfirmTitle')}}
      v-card-text
        p {{$t('detailTab.automaticOrderingConfirmText1')}}
        v-alert.mb-0(type="warning" text dense icon="fas fa-exclamation-triangle") {{$t('detailTab.automaticOrderingConfirmText2')}}
      v-card-actions
        v-spacer
        v-btn(text @click="onAutomaticOrderingCancel" :disabled="automaticOrderingSaveInProgress") {{$t('buttons.cancelButtonLabel')}}
        v-btn(color="error" :loading="automaticOrderingSaveInProgress" @click="onAutomaticOrderingConfirm") {{$t('detailTab.automaticOrderingConfirmButton')}}
  v-card(flat)
    v-skeleton-loader.sloader(v-if="fetchInProgress" type="list-item-two-line@3")
    template(v-else )
      v-card-text.pt-6
        v-form(ref="form" v-model="isFormValid" :lazy-validation="true" :disabled="isSaveInProgress" @submit.prevent )
          v-row
            v-col.py-0(cols="12" md="6")
              v-text-field(v-model="firstName" :label="` ${$t('detailTab.firstNameLabel')}`" :rules="firstNameRules" outlined type="text" clearable)
            v-col.py-0(cols="12" md="6")
              v-text-field(v-model="familyName" :label="` ${$t('detailTab.familyNameLabel')}`" :rules="familyNameRules" outlined)
          v-row
            v-col.py-0(cols="12" md="6")
              v-text-field(v-model="phoneNumber" :label="` ${$t('detailTab.phoneNumberLabel')}`" outlined hide-details :rules="validatePhoneNumber")
          v-list
            v-subheader {{$t('detailTab.emailLabel')}}
            v-list-item(v-for="(item, idx) in emailList" :key="idx")
              v-list-item-content
                v-list-item-title(v-if="!item.isEdit") {{ item.email }}
                v-text-field(v-if="item.isEdit" v-model="item.email" :rules="emailRules" autofocus  @keyup.enter="onSaveChangesClick(idx, item)" )
              v-list-item-action
                v-row.pa-0(v-if="item.isEdit" align="end")
                  v-col.pa-0
                    v-btn(fab text x-small icon @click="onSaveChangesClick(idx, item)" :disabled="!isFormValid")
                      v-icon fa-check
                  v-col.pa-0
                    v-btn(fab text x-small icon @click="onCancelChangesClick(idx)")
                      v-icon fa-times
                v-row.ma-0.pa-0(v-if="!item.isEdit")
                  v-col.pa-0
                    v-btn(fab text x-small icon @click="onEditClick(idx, item)" :disabled="isSaveInProgress || isInEditMode")
                      v-icon fa-pen
                  v-col.pa-0
                    v-btn(fab text x-small icon @click="onDeleteClick(idx)" :disabled="isSaveInProgress")
                      v-icon fa-trash
        v-btn(@click="onAddButtonClick" :disabled="!isFormValid || isInEditMode || isSaveInProgress || fetchInProgress" :loading="isSaveInProgress") {{$t('buttons.addEmailButtonLabel')}}

    v-card-actions.pr-4
      v-spacer
      v-btn(color="primary" :disabled="!isFormValid || isInEditMode || fetchInProgress" :loading="isSaveInProgress" @click="onSubmitButtonClick") {{$t('buttons.saveButtonLabel')}}
    v-card-text.pt-0(v-if="!fetchInProgress && showAutomaticOrderingSwitch")
      v-divider.mb-2
      v-subheader.pl-0 {{$t('detailTab.settingsTitle')}}
      v-switch.mt-0(v-model="automaticOrdering"
        :label="`${$t('detailTab.automaticOrderingLabel')}`"
        :hint="`${$t('detailTab.automaticOrderingHint')}`"
        persistent-hint
        :loading="automaticOrderingSaveInProgress"
        :disabled="automaticOrderingSaveInProgress || isSaveInProgress"
        @change="onAutomaticOrderingChange")
</template>

<script lang="ts">

import Vue from "vue";
import {Component, Prop} from "vue-property-decorator";
import type {BoarderDetailResponse, BoarderResponse} from "@/types/service-response";
import {Action} from "vuex-class";
import {TranslateResult} from "@/i18n";
import i18n from "@/plugins/i18n";
import {
  FETCH_BOARDER_DETAIL,
  FETCH_BOARDER_SETTINGS,
  FETCH_TENANT_NAME,
  UPDATE_BOARDER_DETAIL,
  UPDATE_BOARDER_SETTINGS
} from "@/store/action-types";

const AUTO_RESERVATION_OPT_OUT_KEY = 'boarder.auto_reservation_opt_out'
const AUTOMATICALLY_RESERVE_ALL_KEY = 'tenant.automatically_reserve_all'

type BoarderSettingsItem = { key: string, type: string, value: string | null }
import {
  FetchBoarderDetailPayload,
  UpdateBoarderDetailPayload,
  UpdateBoarderDetailPhoneNumberPayload
} from "@/types/payload";
import {AccountEmail} from "@/model/AccountEmail";
import {AccountEmailItem} from "@/types";

function valueIsRequired (v: string): boolean | TranslateResult {
  return !!v || i18n.t('validation.valueIsRequired')
}

function validateEmail (v: string): boolean | TranslateResult {
  if (v) {
    return /.+@.+\..+/.test(v) || i18n.t('validation.emailMustBeValid')
  }
  return true
}

function validatePhoneNumber (v: string): boolean | TranslateResult {
  if (v) {
    return  /^[+]?[\d\s]+$/.test(v) || i18n.t('validation.phoneNumberMustBeValid')
  }
  return true
}

@Component({
  components: {}
})

export default class DetailDialog extends Vue {
  $refs!: {
    form: Vue & { validate: () => boolean }
  }

  private emailRules = [
    valueIsRequired,
    validateEmail,
  ]
  private firstNameRules = [
    valueIsRequired,
  ]
  private familyNameRules = [
    valueIsRequired,
  ]
  private validatePhoneNumber = [
    validatePhoneNumber
  ]
  private isFormValid: boolean = false
  private isSaveInProgress: boolean = false
  private fetchInProgress: boolean = false
  private showError: boolean = false
  private errorMessage: TranslateResult | null = null
  private firstName: string = ''
  private familyName: string = ''
  private phoneNumber: string = ''
  private emailList: AccountEmail[] = []
  private originalEmailList: AccountEmail[] = []
  private showAutomaticOrderingSwitch: boolean = false
  private automaticOrdering: boolean = true
  private showAutomaticOrderingConfirm: boolean = false
  private automaticOrderingSaveInProgress: boolean = false
  private showSuccess: boolean = false
  private successMessage: TranslateResult | null = null

  @Prop({type: Object, required: false}) readonly boarder!: BoarderResponse

  @Action(FETCH_BOARDER_DETAIL) fetchBoarderDetails!: ({ (payload: FetchBoarderDetailPayload): Promise<BoarderDetailResponse> })
  @Action(UPDATE_BOARDER_DETAIL) updateBoarderDetails!: ({ (payload: UpdateBoarderDetailPayload): Promise<BoarderDetailResponse> })
  @Action(FETCH_BOARDER_SETTINGS) fetchBoarderSettings!: ({ (payload: { boarderId: number }): Promise<BoarderSettingsItem[]> })
  @Action(UPDATE_BOARDER_SETTINGS) updateBoarderSettings!: ({ (payload: { boarderId: number, settings: Array<{ key: string, type: string, value: string }> }): Promise<BoarderSettingsItem[]> })
  @Action(FETCH_TENANT_NAME) fetchTenantSettings!: ({ (): Promise<Array<{ key: string, value: string | null }>> })

  get isInEditMode (): boolean {
    return !!this.emailList.find((item: AccountEmail) => item.isEdit)
  }

  async fetchDetailsAboutBoarder(): Promise<void> {
    this.fetchInProgress = true

    let payload: FetchBoarderDetailPayload = {
      id: this.boarder.person.id
    }

    try {
      const response: BoarderDetailResponse = await this.fetchBoarderDetails(payload)
      this.firstName = response.firstName
      this.familyName = response.familyName
      this.phoneNumber = response.phoneNumbers[0]?.phoneNumber
      this.emailList = AccountEmail.createFromBoarderDetailResponse(response)
      this.originalEmailList = AccountEmail.createFromBoarderDetailResponse(response)
      await this.fetchAutomaticOrderingState()
    } catch (e) {
      this.showError = true
      this.errorMessage = this.$t('errors.fetchDataError')
    } finally {
      this.fetchInProgress = false
    }
  }

  // the switch is shown only when the tenant orders automatically at all;
  // a failure here must not fail the whole detail — just hide the switch
  async fetchAutomaticOrderingState(): Promise<void> {
    try {
      const [tenantSettings, boarderSettings] = await Promise.all([
        this.fetchTenantSettings(),
        this.fetchBoarderSettings({boarderId: this.boarder.id})
      ])

      const reserveAll = tenantSettings.find((i) => i.key === AUTOMATICALLY_RESERVE_ALL_KEY)
      this.showAutomaticOrderingSwitch = reserveAll?.value === 'true'

      const optOut = boarderSettings.find((i) => i.key === AUTO_RESERVATION_OPT_OUT_KEY)
      this.automaticOrdering = optOut?.value !== 'true'
    } catch (e) {
      this.showAutomaticOrderingSwitch = false
    }
  }

  // the switch saves on flip, independently of the detail form's save button;
  // turning automatic ordering OFF needs an explicit confirmation first
  onAutomaticOrderingChange(value: boolean): void {
    if (!value) {
      this.showAutomaticOrderingConfirm = true
      return
    }

    this.persistAutomaticOrdering(true)
  }

  async onAutomaticOrderingConfirm(): Promise<void> {
    await this.persistAutomaticOrdering(false)
    this.showAutomaticOrderingConfirm = false
  }

  onAutomaticOrderingCancel(): void {
    this.showAutomaticOrderingConfirm = false
    this.automaticOrdering = true
  }

  private async persistAutomaticOrdering(enabled: boolean): Promise<void> {
    this.automaticOrderingSaveInProgress = true
    try {
      await this.updateBoarderSettings({
        boarderId: this.boarder.id,
        settings: [{
          key: AUTO_RESERVATION_OPT_OUT_KEY,
          type: 'boolean',
          value: enabled ? 'false' : 'true'
        }]
      })
      this.automaticOrdering = enabled
      this.successMessage = this.$t(enabled ? 'detailTab.automaticOrderingEnabledMessage' : 'detailTab.automaticOrderingDisabledMessage')
      this.showSuccess = true
    } catch (e) {
      // a failed PUT must not leave the switch showing a state the server never stored
      this.automaticOrdering = !enabled
      this.errorMessage = this.$t('errors.saveErrorMessage')
      this.showError = true
    } finally {
      this.automaticOrderingSaveInProgress = false
    }
  }

  onSaveChangesClick(idx: number, item: AccountEmail) {
    this.$refs.form.validate()
    if(this.isFormValid && item.email) {
      this.emailList.splice(idx, 1, item.update!(!item.isEdit))
      this.originalEmailList.splice(idx, 1, item.update!(!item.isEdit))
    }
  }

  onCancelChangesClick(idx: number) {
    if(this.originalEmailList[idx]) {
      this.emailList.splice(idx, 1, this.originalEmailList[idx])
    } else {
      this.emailList.splice(idx, 1)
      this.originalEmailList.splice(idx, 1)
    }
  }

  onEditClick(idx: number, item: AccountEmail) {
    this.emailList.splice(idx, 1, item.update!(!item.isEdit))
  }

  onDeleteClick(idx: number) {
    this.emailList.splice(idx, 1)
    this.originalEmailList.splice(idx, 1)
  }

  onAddButtonClick() {
    this.emailList.unshift(AccountEmail.createEmpty())
  }

  async onSubmitButtonClick(): Promise<void> {
    this.isSaveInProgress = true
    this.$emit('isSaveInProgress')

    const payloadEmails: AccountEmailItem[] =  this.emailList.map((item: AccountEmail) => {
      return {email: item.email}
    })

    const payloadPhoneNumbers: UpdateBoarderDetailPhoneNumberPayload[] = this.phoneNumber ? [{ phoneNumber: this.phoneNumber}] : []

    const userPayload: UpdateBoarderDetailPayload = {
      id: this.boarder.person.id,
      firstName: this.firstName,
      familyName: this.familyName,
      phoneNumbers: payloadPhoneNumbers,
      emails: payloadEmails
    }

    try {
      await this.updateBoarderDetails(userPayload)
    } catch (e) {
      this.errorMessage = this.$t('errors.saveErrorMessage')
      this.showError = true
    } finally {
      this.isSaveInProgress = false
      this.$emit('isSaveInProgress')
    }
  }

  mounted(): void {
    this.fetchDetailsAboutBoarder()
  }

}
</script>

<style lang="stylus" scoped>

</style>
